import {
  FunnelTemplate,
  funnelTemplateIDFromPresetID,
  funnelTemplatePresetID,
} from '@marketing-milk/interfaces'
import Preset from 'app/interface/data/funnel/preset'
import Redux from 'app/interface/redux'
import { funnelTemplateService } from 'app/service/funnel-template'
import { presetFromTemplate } from 'app/lib/funnel-template'
import { BuiltInPresets } from 'app/redux/default/data/funnel/presets'
import Store from 'app/redux/store'
import { bindActions } from 'redux-zero/utils'

/**
 * Templates in the order the server lists them (`name ASC`, which in Postgres's
 * en_US collation ignores case), so a card saved in this session sits where it
 * will be on the next load.
 */
const byName = (a: Preset, b: Preset) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

const withTemplate = (presets: Preset[], template: Preset): Preset[] => {
  const builtIns = presets.filter(preset => funnelTemplateIDFromPresetID(preset.id) === null)
  const templates = presets.filter(preset => funnelTemplateIDFromPresetID(preset.id) !== null)
  return [...builtIns, ...[...templates, template].sort(byName)]
}

const Presets = bindActions(
  () => ({
    /**
     * If a template is saved or deleted while this GET is in flight, the
     * response may predate it and `update` would undo it. Both of those write
     * the list only after the server has, so one refetch is always current.
     */
    load: async () => {
      const startedAt = +new Date()
      const templates = await funnelTemplateService.getAll()
      const changedMeanwhile =
        (Store.getState() as Redux).data.funnel.presets.timestamp >= startedAt
      Presets.update(changedMeanwhile ? await funnelTemplateService.getAll() : templates)
    },

    /**
     * Built-ins first, then the user's templates. Both the grid and
     * FunnelActions.setPreset read this one array, and setPreset resolves a
     * preset by `.find(x => x.id === id)` — so templates only need their
     * namespaced ids to coexist with the built-ins' 1-4.
     */
    update: (state: any, templates: FunnelTemplate[]) => ({
      data: {
        ...state.data,
        funnel: {
          ...state.data.funnel,
          presets: {
            data: [...BuiltInPresets, ...templates.map(presetFromTemplate)],
            loading: false,
            timestamp: +new Date(),
          },
        },
      },
    }),

    /** Adds one freshly saved template, so saving does not refetch the list. */
    add: (state: any, template: FunnelTemplate) => ({
      data: {
        ...state.data,
        funnel: {
          ...state.data.funnel,
          presets: {
            ...state.data.funnel.presets,
            data: withTemplate(state.data.funnel.presets.data, presetFromTemplate(template)),
            timestamp: +new Date(),
          },
        },
      },
    }),

    /** Takes a template id, not a picker id — the offset is applied here. */
    remove: (state: any, templateID: number) => ({
      data: {
        ...state.data,
        funnel: {
          ...state.data.funnel,
          presets: {
            ...state.data.funnel.presets,
            data: state.data.funnel.presets.data.filter(
              (preset: { id: number }) => preset.id !== funnelTemplatePresetID(templateID)
            ),
            timestamp: +new Date(),
          },
        },
      },
    }),
  }),
  Store
)

export default Presets
